import { isValid, lightFormat } from 'date-fns';
import { ref, Ref, unref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

export interface Translatable {
  translations?: {
    language?: string;
    type?: string;
    name?: string;
  }[];
}

export default {
  groupBy(objectArray: any[], property: string) {
    return objectArray.reduce(function (acc, obj) {
      const key = obj[property];
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});
  },

  getTranslation(
    translations:
      | Array<{ language?: string; type?: string }>
      | undefined
      | null,
    locale: string,
  ): Record<string, string> | undefined {
    if (!translations || translations.length === 0 || !locale) {
      return undefined;
    }

    const exactMatch = translations.find(
      (translation) => translation.language === locale,
    );
    if (exactMatch) {
      return exactMatch;
    }

    const shorthandMatch = translations.find((translation) =>
      translation.language?.startsWith(locale.substring(0, 2)),
    );
    if (shorthandMatch) {
      return shorthandMatch;
    }

    const englishMatch = translations.find((translation) =>
      translation.language?.startsWith('en'),
    );
    if (englishMatch) {
      return englishMatch;
    }

    const defaultMatch = translations.find(
      (translation) => translation.type === 'default',
    );
    if (defaultMatch) {
      return defaultMatch;
    }

    return translations?.[0];
  },

  getItemTranslation<T extends Translatable>(
    item: T | null | undefined,
    locale: string,
  ): string | undefined {
    return this.getTranslation(item?.translations, locale)?.name;
  },

  getItemDescription(
    item:
      | {
          descriptions: Array<{
            language?: string;
            type?: string;
            description?: string;
          }>;
        }
      | null
      | undefined,
    locale: string,
  ): string | undefined {
    return this.getTranslation(item?.descriptions, locale)?.description;
  },

  stringToDate(dateString: string): Date | undefined {
    const parsed = new Date(dateString);
    if (!dateString || isNaN(parsed.getTime())) {
      return undefined;
    }
    return parsed;
  },

  dateToString(
    inputDate: Date | string | undefined | null,
  ): string | undefined {
    if (!inputDate) {
      return undefined;
    }

    const date =
      typeof inputDate === 'string' ? new Date(inputDate) : inputDate;

    if (!isValid(date)) {
      return undefined;
    }

    return lightFormat(date, 'yyyy-MM-dd');
  },

  iriToID(iri: string | undefined): string | undefined {
    const split = iri?.split('/');
    return split?.[split.length - 1];
  },

  // Extract ID from IRI string or object
  extractID(
    iri: string | undefined | null | Record<string, any> | number,
  ): string | undefined {
    if (iri === undefined || iri === null) {
      return undefined;
    }

    if (typeof iri === 'string') {
      return this.iriToID(iri);
    }

    if (typeof iri === 'number') {
      return iri.toString(10);
    }

    if ('id' in iri) {
      return iri.id;
    }

    if ('@id' in iri) {
      return this.iriToID(iri['@id']);
    }

    return undefined;
  },

  getIRI(
    object: string | undefined | null | Record<string, any> | number,
    path: string,
  ): string | undefined {
    const id = this.extractID(object);
    if (id === undefined) {
      return undefined;
    }
    if (!path.endsWith('/')) {
      path += '/';
    }
    return path + id.toString();
  },

  dt(path: string | Ref<string>): Ref<string> {
    const { t, locale } = useI18n({ useScope: 'global' });
    const tr = ref(t(unref(path)));
    watch(
      () => [unref(locale), unref(path)],
      () => (tr.value = t(unref(path))),
    );
    return tr;
  },

  formatTimeRange(start: string, end: string, locale: string) {
    const formatter = new Intl.DateTimeFormat(locale, {
      timeStyle: 'short',
    });
    const startDate = new Date(0, 0, 0, ...start.split(':').map(Number));
    const endDate = new Date(0, 0, 0, ...end.split(':').map(Number));
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return '';
    }
    return formatter.formatRange(startDate, endDate);
  },

  formatDateRange(
    start: string | Date,
    end: string | Date,
    locale: string,
    options?: Intl.DateTimeFormatOptions,
  ) {
    const formatter = new Intl.DateTimeFormat(locale, {
      dateStyle: 'long',
      ...options,
    });
    const startDate = typeof start === 'string' ? new Date(start) : start;
    const endDate = typeof end === 'string' ? new Date(end) : end;
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return '';
    }
    return formatter.formatRange(startDate, endDate);
  },
};
